import { useCallback } from 'react'
import { useMutation, useQuery } from '@apollo/client/react'
import { getWebClient } from '@/services/ApolloClient'
import {
  AddUserToVoucherWaitlistRequest,
  AddUserToWaitlistMutation,
  AddUserToWaitlistMutationVariables,
  GetClaimReasonsQuery,
  TheatricalShowtime,
  VoucherWaitlistResponse,
} from '@/types/codegen-federation'
import { useLocale } from '@/utils/LocaleUtil'
import { addUserToWaitlistMutation, getClaimReasonsQuery } from './queries'

export function getSortedShowtimes(showtimes: TheatricalShowtime[]): TheatricalShowtime[] {
  return showtimes.toSorted((a, b) => {
    if (a.utcStartTime && !b.utcStartTime) return -1
    if (!a.utcStartTime && b.utcStartTime) return 1
    if (!a.utcStartTime || !b.utcStartTime) return 0
    if (a.utcStartTime < b.utcStartTime) return -1
    if (a.utcStartTime > b.utcStartTime) return 1
    return 0
  })
}

export type ClaimReason = NonNullable<NonNullable<NonNullable<GetClaimReasonsQuery>['claimReasons']>[number]>

export function useClaimReasons() {
  const response = useQuery<GetClaimReasonsQuery>(getClaimReasonsQuery, {
    errorPolicy: 'all',
  })

  return {
    ...response,
    claimReasons: (response?.data?.claimReasons ?? []) as ClaimReason[],
  }
}

export type AddUserToWaitlistResponse = NonNullable<AddUserToWaitlistMutation>

export function useClaimTicketMutation() {
  const { locale } = useLocale()
  const client = getWebClient({ locale })
  const [claimTicketInternal, { data: claimTicketResponse, error: claimTicketError }] = useMutation<
    { addUserToWaitlist: VoucherWaitlistResponse },
    AddUserToWaitlistMutationVariables
  >(addUserToWaitlistMutation, { client, errorPolicy: 'all' })

  const claimTicket = useCallback(
    (input: AddUserToVoucherWaitlistRequest) => {
      return claimTicketInternal({ variables: { input } })
    },
    [claimTicketInternal],
  )

  return { claimTicket, claimTicketResponse, claimTicketError }
}
